import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { ONE_SECOND_MS, SESSION_TIMING, SESSION_WARNING_REASONS } from '../constants/Session';
import { extendsSession, getAccessTokenExpiryMs, getSessionTimeline } from '../utils/tokenExpiry';
import { getSessionRenewers } from '../services/sessionRenewers';
import { forceLogout } from '../services/sessionLogout';
import { startActivityTracking, stopActivityTracking, wasActiveWithin } from '../services/activityTracker';

// setTimeout fires at once for delays above this (about 24.8 days)
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

// Calls onDue at the given time. Delays too long for one setTimeout are
// covered by re-arming until the time is reached. Returns the canceller.
const scheduleAt = (at, onDue) => {
  let timer = null;
  const arm = () => {
    const delay = Math.min(Math.max(at - Date.now(), 0), MAX_TIMER_DELAY_MS);
    timer = setTimeout(() => (Date.now() >= at ? onDue() : arm()), delay);
  };
  arm();
  return () => clearTimeout(timer);
};

// Rejects when promise has not settled within ms. The late answer is
// dropped, so it cannot be applied after the caller moved on.
const withTimeout = (promise, ms) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Session renewal timed out after ${ms} ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

// Arms the decision point and the forced logout for the current access
// token. Background tabs throttle timers and a sleeping laptop pauses them,
// so the timeline is re-checked whenever the tab regains focus. The
// decision fires at most once per token, with the logout deadline. Keyed on
// the token and its expiry, not the auth data object, so saving the same
// token again does not re-arm the decision.
const useSessionTimeline = (accessToken, expiresAtMs, sessionExpired, onDecisionDue) => {
  const onDecisionDueRef = useRef(onDecisionDue);
  onDecisionDueRef.current = onDecisionDue;

  useEffect(() => {
    if (!expiresAtMs || sessionExpired) return undefined;

    const { decideAt, logoutAt } = getSessionTimeline(expiresAtMs, Date.now(), SESSION_TIMING);
    let decided = false;
    const checkTimeline = () => {
      const now = Date.now();
      if (now >= logoutAt) {
        forceLogout();
      } else if (now >= decideAt && !decided) {
        decided = true;
        onDecisionDueRef.current(logoutAt);
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkTimeline();
    };

    checkTimeline();
    const cancelTimers = [decideAt, logoutAt].map((at) => scheduleAt(at, checkTimeline));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkTimeline);

    return () => {
      cancelTimers.forEach((cancel) => cancel());
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkTimeline);
    };
  }, [accessToken, expiresAtMs, sessionExpired]);
};

// Whole seconds left until the deadline, re-rendering once a second while
// set. Read from the clock on every render, so the first frame of a new
// deadline is already correct.
const useSecondsLeft = (deadline) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!deadline) return undefined;
    const interval = setInterval(() => setTick((tick) => tick + 1), ONE_SECOND_MS);
    return () => clearInterval(interval);
  }, [deadline]);

  return deadline ? Math.max(Math.ceil((deadline - Date.now()) / ONE_SECOND_MS), 0) : 0;
};

// Listens for user interaction while the chat page is mounted, so the
// decision point can tell an active user from an idle one.
const useActivityTracking = () => {
  useEffect(() => {
    startActivityTracking();
    return stopActivityTracking;
  }, []);
};

// Renewals are async; this lets their callbacks skip state updates once the
// page has unmounted (e.g. after a forced logout).
const useIsMounted = () => {
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  return isMountedRef;
};

// Session timeout for the chat page. Near the access token expiry an active
// user is renewed silently; an idle user, or one whose silent renewal
// failed, gets the warning popup to continue or cancel. The session is
// logged out when the countdown runs out or a user-requested renewal fails.
const useSessionTimeout = () => {
  const authData = useAuthStore((s) => s.authData);
  const setAuthData = useAuthStore((s) => s.setAuthData);
  const sessionExpired = useAuthStore((s) => s.sessionExpired);
  // { reason, deadline } while the popup is showing, null otherwise
  const [warning, setWarning] = useState(null);
  const [isRenewing, setIsRenewing] = useState(false);
  const isMountedRef = useIsMounted();
  const accessToken = authData?.accessToken;
  const expiresAtMs = getAccessTokenExpiryMs(authData);

  useActivityTracking();

  // A new token starts a fresh timeline with the popup closed
  useEffect(() => {
    setWarning(null);
    setIsRenewing(false);
  }, [accessToken, expiresAtMs]);

  // Runs the renewer and merges the fresh token into the latest auth data
  // (other fields may have changed while it was in flight), which restarts
  // the timeline for the new token. Throws when the renewal does not extend
  // the session, so it is handled like any other failed renewal instead of
  // re-triggering the decision straight away. The save is skipped when the
  // page unmounted or the countdown already logged out in the meantime, so a
  // late answer cannot revive an expired session. Renewal errors are left to
  // the caller.
  const applyRenewal = useCallback(
    async (renewer) => {
      const renewed = await withTimeout(renewer.renew(authData), SESSION_TIMING.RENEWAL_TIMEOUT_MS);
      if (!extendsSession(getAccessTokenExpiryMs(renewed), Date.now(), SESSION_TIMING)) {
        throw new Error('Renewed token does not extend the session');
      }
      const { authData: latest, sessionExpired: expiredMeanwhile } = useAuthStore.getState();
      if (isMountedRef.current && !expiredMeanwhile) {
        setAuthData({ ...latest, ...renewed });
      }
    },
    [authData, setAuthData, isMountedRef]
  );

  // Tries each renewer in order until one succeeds. False when all failed.
  const renewWithFallback = useCallback(
    async (renewers) => {
      for (const renewer of renewers) {
        try {
          await applyRenewal(renewer);
          return true;
        } catch (error) {
          console.warn('Session renewal failed, trying the next renewer:', error);
        }
      }
      return false;
    },
    [applyRenewal]
  );

  // Opens the popup with why it is showing and when the countdown ends.
  const showWarning = useCallback(
    (reason, deadline) => {
      if (isMountedRef.current) setWarning({ reason, deadline });
    },
    [isMountedRef]
  );

  // Decision point, once per token. Idle user: ask via the popup. Active
  // user: renew silently in place; when nothing can renew this session or
  // every renewal fails, fall back to the popup instead of logging out.
  const handleDecisionDue = useCallback(
    async (deadline) => {
      if (!wasActiveWithin(SESSION_TIMING.IDLE_THRESHOLD_MS)) {
        showWarning(SESSION_WARNING_REASONS.IDLE, deadline);
        return;
      }
      const renewed = await renewWithFallback(getSessionRenewers(authData));
      if (!renewed) showWarning(SESSION_WARNING_REASONS.RENEWAL_FAILED, deadline);
    },
    [authData, renewWithFallback, showWarning]
  );

  useSessionTimeline(accessToken, expiresAtMs, sessionExpired, handleDecisionDue);
  const secondsLeft = useSecondsLeft(warning?.deadline);

  // Popup's Continue button. Every renewer that applies is tried again in
  // order, in place. Logs out when all of them failed or none applies (e.g.
  // the website flow without its parent window).
  const continueSession = useCallback(async () => {
    setIsRenewing(true);
    const renewed = await renewWithFallback(getSessionRenewers(authData));
    if (!isMountedRef.current) return;
    if (!renewed) {
      console.error('Every session renewal failed, logging out');
      forceLogout();
    }
    setIsRenewing(false);
  }, [authData, renewWithFallback, isMountedRef]);

  // Popup's Cancel button: closes the popup only. The countdown keeps
  // running and still logs out at its deadline.
  const dismissWarning = useCallback(() => setWarning(null), []);

  return {
    sessionExpired,
    isWarningVisible: Boolean(warning) && !sessionExpired,
    warningReason: warning?.reason ?? null,
    secondsLeft,
    isRenewing,
    continueSession,
    dismissWarning,
  };
};

export default useSessionTimeout;
